import { ArrowGo, Heart, HeartFilled, Share } from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"

import { AuthButton } from "@/components/auth-button"
import { ContentActionsMenu } from "@/components/content-actions-menu"
import { PageHeader } from "@/components/page-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import { jobPath, jobsEditPath, jobsPath } from "@/routes"

interface Job {
  id: number | string
  title: string
  company_name: string
  location_type?: string
  category?: string
  apply_url?: string
  apply_instructions?: string
  source?: string
  source_url?: string
  source_name?: string
  description: string // HTML
  status?: string
  expires_at?: string
  author?: { id: number; name: string; verified?: boolean }
  neighborhood?: { id: number; name: string } | null
  city?: { id: number; name: string } | null
  created_at?: string
  is_favorited?: boolean
  favorites_count?: number
  views_count?: number
}

interface Props {
  job: Job
  can_edit?: boolean
}

const LOCATION_LABELS: Record<string, string> = {
  remote: "Remoto",
  hybrid: "Híbrido",
  onsite: "Presencial",
}

const CATEGORY_LABELS: Record<string, string> = {
  tech: "Tecnología",
  gastronomia: "Gastronomía",
  servicios: "Servicios",
  administracion: "Administración",
  ventas: "Ventas",
  educacion: "Educación",
  salud: "Salud",
  construccion: "Construcción",
  limpieza: "Limpieza",
  transporte: "Transporte",
}

export default function JobShow({ job, can_edit = false }: Props) {
  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  const jobId = Number(job.id)
  const storedFavorite = getStoreFavorite("job", jobId)
  const storedCount = getStoreCount("job", jobId)
  const actualFavorited = storedFavorite ?? job.is_favorited
  const actualFavoritesCount = storedCount ?? job.favorites_count ?? 0

  const handleShare = async () => {
    try {
      const url = `${window.location.origin}${jobPath(job.id)}`
      const title = `${job.title} - ${job.company_name}`
      if (navigator.share) {
        await navigator.share({ title, url })
      } else if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        await navigator.clipboard.writeText(url)
      }
    } catch {
      // no-op
    }
  }

  const handleEdit = () => router.visit(jobsEditPath(job.id))
  const handleDelete = () => {
    if (!confirm("¿Eliminar este curro? Esta acción no se puede deshacer."))
      return
    router.delete(jobPath(job.id), {
      preserveScroll: false,
      onSuccess: () => router.visit(jobsPath()),
    })
  }
  const handleFavorite = () => {
    const newValue = !actualFavorited
    const newCount = newValue
      ? actualFavoritesCount + 1
      : Math.max(0, actualFavoritesCount - 1)

    setStoreFavorite("job", jobId, newValue)
    setStoreCount("job", jobId, newCount)

    router.post(
      `${jobPath(job.id)}/favorite`,
      {},
      {
        preserveScroll: true,
        onError: () => {
          setStoreFavorite("job", jobId, !newValue)
          setStoreCount(
            "job",
            jobId,
            newValue ? Math.max(0, newCount - 1) : newCount + 1,
          )
        },
      },
    )
  }

  return (
    <AppLayout
      showEditButton={can_edit}
      showDeleteButton={can_edit}
      showBackButton={false}
      showShareButton={false}
      showFavoriteButton={false}
      isFavorite={actualFavorited}
      onShare={() => {
        void handleShare()
      }}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onFavorite={handleFavorite}
    >
      <Head title={`${job.title} - ${job.company_name}`} />

      <div className="mx-auto max-w-6xl space-y-4 md:space-y-6 md:p-6">
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton
            onBack={() => router.visit(jobsPath())}
            showEdit={can_edit}
            showDelete={can_edit}
            showShare={true}
            showFavorite={true}
            isFavorite={actualFavorited}
            editHref={jobsEditPath(job.id)}
            onShare={() => {
              void handleShare()
            }}
            onDelete={handleDelete}
            onFavorite={handleFavorite}
          />
        </div>
        <div className="px-4">
          <div className="text-primary flex flex-wrap items-center gap-2 text-[13px] font-medium tracking-[0.13px]">
            <Link href={jobsPath()}>
              <div className="flex items-center gap-1 pt-[3px]">
                <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
                Curros
              </div>
            </Link>
          </div>
        </div>

        <div className="">
          <div className="flex items-start justify-between gap-2 px-[10px]">
            <div>
              <p className="text-muted-foreground text-[14px] font-medium">
                {job.company_name}
              </p>
              <h1 className="text-foreground text-[21px] leading-[28px] font-semibold tracking-[0px]">
                {job.title}
              </h1>
            </div>
            <ContentActionsMenu
              contentType="Job"
              contentId={Number(job.id)}
              authorId={job.author?.id ?? 0}
              authorName={job.author?.name ?? "Autor"}
            />
          </div>

          {/* Badges */}
          <div className="mt-2 flex flex-wrap items-center gap-2 px-[10px]">
            {job.location_type && (
              <Badge variant="secondary">
                {LOCATION_LABELS[job.location_type] ?? job.location_type}
              </Badge>
            )}
            {job.category && (
              <Badge variant="outline">
                {CATEGORY_LABELS[job.category] ?? job.category}
              </Badge>
            )}
            {job.source === "external" && job.source_name && (
              <Badge variant="outline">{job.source_name}</Badge>
            )}
            {job.neighborhood && (
              <Badge variant="outline">{job.neighborhood.name}</Badge>
            )}
          </div>

          {/* Actions */}
          <div className="mb-2 flex flex-wrap items-center gap-[14px] p-[10px]">
            <div className="flex items-center gap-[4px]">
              <AuthButton
                variant="ghost"
                size="icon"
                className="h-[14px] w-[16px]"
                onClick={() => void handleFavorite()}
              >
                {actualFavorited ? (
                  <HeartFilled className="text-destructive size-4.5 w-auto fill-current" />
                ) : (
                  <Heart className="size-4.5 w-auto" />
                )}
                <span className="sr-only">
                  {actualFavorited
                    ? "Quitar de favoritos"
                    : "Agregar a favoritos"}
                </span>
              </AuthButton>
              <span className="text-muted-foreground text-[13px] tracking-[0.20px]">
                {actualFavoritesCount}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-[20px] w-[20px]"
              onClick={() => void handleShare()}
            >
              <Share className="size-5" />
              <span className="sr-only">Compartir</span>
            </Button>
          </div>

          {/* Apply Section */}
          {(job.apply_url ?? job.apply_instructions) && (
            <div className="border-border bg-card mx-[10px] mb-4 rounded-lg border p-4">
              <h3 className="text-foreground mb-2 text-[16px] font-semibold">
                Cómo aplicar
              </h3>
              {job.apply_url && (
                <a
                  href={job.apply_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary mb-2 inline-flex items-center gap-1 text-[14px] font-medium hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  Aplicar ahora
                </a>
              )}
              {job.apply_instructions && (
                <p className="text-muted-foreground text-[13px]">
                  {job.apply_instructions}
                </p>
              )}
            </div>
          )}

          {/* Description */}
          <div
            className="interview-content [&_iframe]:aspect-video [&_iframe]:w-full"
            dangerouslySetInnerHTML={{ __html: job.description }}
          />
        </div>
      </div>
    </AppLayout>
  )
}
